import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../App';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import InfoChip from './InfoChip';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');

function CardSetupForm({ token, onAdded }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!stripe || !elements) return;

    setLoading(true);
    try {
      const setupRes = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tenants/payments/setup-card`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const setupData = await setupRes.json().catch(() => ({}));
      if (!setupRes.ok) throw new Error(setupData.error || 'Unable to start card setup.');

      const cardElement = elements.getElement(CardElement);
      const { setupIntent, error: stripeError } = await stripe.confirmCardSetup(setupData.client_secret, {
        payment_method: { card: cardElement }
      });

      if (stripeError) throw new Error(stripeError.message || 'Card setup failed.');

      const paymentMethodId = setupIntent?.payment_method;
      if (!paymentMethodId) throw new Error('Payment method not available.');

      const saveRes = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tenants/payment-methods`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          type: 'card',
          label: 'Card',
          is_default: true,
          stripe_payment_method_id: paymentMethodId
        })
      });
      const saveData = await saveRes.json().catch(() => ({}));
      if (!saveRes.ok) throw new Error(saveData.error || 'Unable to save payment method.');

      setSuccess('Card added.');
      if (onAdded) onAdded();
    } catch (err) {
      setError(err.message || 'Unable to add card.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ marginTop: '0.5rem' }}>
      {error && <p className="error-message">{error}</p>}
      {success && <p className="status-text">{success}</p>}
      <div className="form-control" style={{ padding: '0.75rem' }}>
        <CardElement options={{ hidePostalCode: true }} />
      </div>
      <button className="primary-button" type="submit" disabled={!stripe || loading} style={{ marginTop: '0.75rem' }}>
        {loading ? 'Saving…' : 'Add card'}
      </button>
    </form>
  );
}

/* Inner form that mounts INSIDE an Elements provider that already has the clientSecret */
function PadSetupInnerForm({ token, onAdded }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!stripe || !elements) return;

    setLoading(true);
    try {
      const { error: stripeError, setupIntent } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: window.location.href
        },
        redirect: 'if_required'
      });

      if (stripeError) throw new Error(stripeError.message || 'PAD setup failed.');

      const paymentMethodId = setupIntent?.payment_method;
      if (!paymentMethodId) throw new Error('Payment method not available.');

      const saveRes = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tenants/payment-methods`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          type: 'pad',
          label: 'PAD',
          is_default: false,
          stripe_payment_method_id: paymentMethodId
        })
      });
      const saveData = await saveRes.json().catch(() => ({}));
      if (!saveRes.ok) throw new Error(saveData.error || 'Unable to save PAD method.');

      setSuccess('Bank account added.');
      if (onAdded) onAdded();
    } catch (err) {
      setError(err.message || 'Unable to add bank account.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ marginTop: '0.75rem' }}>
      {error && <p className="error-message">{error}</p>}
      {success && <p className="status-text">{success}</p>}
      <div className="form-control" style={{ padding: '0.75rem' }}>
        <PaymentElement />
      </div>
      <button className="primary-button" type="submit" disabled={!stripe || loading} style={{ marginTop: '0.75rem' }}>
        {loading ? 'Saving…' : 'Add bank account'}
      </button>
    </form>
  );
}

/* Outer wrapper: fetches clientSecret, then mounts Elements+PaymentElement only when ready */
function PadSetupForm({ token, onAdded }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [clientSecret, setClientSecret] = useState('');

  const startSetup = async () => {
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tenants/payments/setup-pad`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Unable to start PAD setup.');
      setClientSecret(data.client_secret || '');
    } catch (err) {
      setError(err.message || 'Unable to start PAD setup.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ marginTop: '1.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
        <h4 style={{ margin: 0 }}>Add a bank account (PAD)</h4>
        <InfoChip label="What is PAD?">
          Pre-Authorized Debit pulls rent directly from your bank account — no card fees. Verification can take 1–2 business days.
        </InfoChip>
      </div>
      {error && <p className="error-message" style={{ marginTop: '0.5rem' }}>{error}</p>}
      {!clientSecret ? (
        <button className="primary-button" onClick={startSetup} disabled={loading} style={{ marginTop: '0.75rem', display: 'block' }}>
          {loading ? 'Setting up…' : 'Add bank account (PAD)'}
        </button>
      ) : (
        <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: 'stripe' } }}>
          <PadSetupInnerForm token={token} onAdded={onAdded} />
        </Elements>
      )}
    </div>
  );
}

const formatCurrency = (value) => {
  const num = Number(value || 0);
  return num.toLocaleString('en-CA', { style: 'currency', currency: 'CAD' });
};

function TenantPayments() {
  const { user, authReady } = useContext(AuthContext);
  const [ledgers, setLedgers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [activeTab, setActiveTab] = useState('rent-due');

  const [receipt, setReceipt] = useState(null);
  const [ledgerLoading] = useState(false);
  const [autopaySaving, setAutopaySaving] = useState(false);
  const [paymentRefreshLoading, setPaymentRefreshLoading] = useState(false);
  const [paymentsReady, setPaymentsReady] = useState(true);
  const [paymentMode, setPaymentMode] = useState('pending');
  const [paymentModeSaving, setPaymentModeSaving] = useState(false);
  const [reportPaidModal, setReportPaidModal] = useState(null); // ledger entry or null
  const [reportPaidMethod, setReportPaidMethod] = useState('e_transfer');
  const [reportPaidReference, setReportPaidReference] = useState('');
  const [reportPaidNote, setReportPaidNote] = useState('');
  const [reportPaidLoading, setReportPaidLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Read tab from URL query params
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab === 'payment-methods') setActiveTab('payment-methods');
    else if (tab === 'payment-history') setActiveTab('payment-history');
    else if (tab === 'rent-due') setActiveTab('rent-due');
  }, [location.search]);

  const updatePaymentMode = async (mode) => {
    setPaymentModeSaving(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tenants/payment-mode`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${user.token}` },
        body: JSON.stringify({ mode })
      });
      if (res.ok) setPaymentMode(mode);
    } catch (err) {
      setError(err.message || 'Unable to update payment mode');
    } finally {
      setPaymentModeSaving(false);
    }
  };

  const fetchPaymentMethods = async (token) => {
    const methodsRes = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tenants/payment-methods`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    const methodsData = methodsRes.ok ? await methodsRes.json().catch(() => ({})) : { methods: [] };
    setPaymentMethods(methodsData.methods || []);
    if ((methodsData.methods || []).length > 0) {
      const defaultMethod = (methodsData.methods || []).find(m => m.is_default) || methodsData.methods[0];
      setSelectedPaymentMethod(defaultMethod?.id ? String(defaultMethod.id) : '');
    }
  };

  const handleMethodAdded = async () => {
    await fetchPaymentMethods(user?.token);
    // Auto-switch to autopay when a method is added
    if (paymentMode !== 'autopay') {
      await updatePaymentMode('autopay');
    }
  };

  const handleMethodRemoved = async (methodId) => {
    const remaining = paymentMethods.filter(m => m.id !== methodId);
    setPaymentMethods(remaining);
    // If last method removed, switch to off_platform
    if (remaining.length === 0 && paymentMode === 'autopay') {
      await updatePaymentMode('off_platform');
    }
  };

  const refreshPaymentsAndLedgers = async (token, silent = false) => {
    if (!token) return;
    if (!silent) {
      setPaymentRefreshLoading(true);
    }
    try {
      const [ledgerRes, paymentsRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tenants/rent-ledger`, {
          headers: { 'Authorization': `Bearer ${token}` },
        }),
        fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tenants/payments`, {
          headers: { 'Authorization': `Bearer ${token}` },
        })
      ]);
      const ledgerData = ledgerRes.ok ? await ledgerRes.json().catch(() => ({})) : { ledgers: [] };
      const paymentsData = paymentsRes.ok ? await paymentsRes.json().catch(() => ({})) : { payments: [] };
      setLedgers(ledgerData.ledgers || []);
      setPayments(paymentsData.payments || []);
      const ready = ledgerData.payments_ready !== false;
      setPaymentsReady(ready);
      if (!ready) {
        setPaymentMethods([]);
        setSelectedPaymentMethod('');
      }
    } catch (err) {
      if (!silent) setError(err.message || 'Failed to refresh payments.');
    } finally {
      if (!silent) {
        setPaymentRefreshLoading(false);
      }
    }
  };



  useEffect(() => {
    if (!authReady) return;
    const fetchPayments = async () => {
      if (!user || user.userType !== 'tenant') {
        navigate('/login');
        return;
      }
      const token = user.token;
      try {
        const [ledgerRes, paymentsRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tenants/rent-ledger`, {
            headers: { 'Authorization': `Bearer ${token}` },
          }),
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tenants/payments`, {
            headers: { 'Authorization': `Bearer ${token}` },
          })
        ]);
        const ledgerData = ledgerRes.ok ? await ledgerRes.json() : { ledgers: [] };
        const paymentsData = paymentsRes.ok ? await paymentsRes.json() : { payments: [] };
        setLedgers(ledgerData.ledgers || []);
        setPayments(paymentsData.payments || []);
        setPaymentMode(ledgerData.payment_mode || 'pending');
        const ready = ledgerData.payments_ready !== false;
        setPaymentsReady(ready);
        if (ready) {
          await fetchPaymentMethods(token);
        } else {
          setPaymentMethods([]);
          setSelectedPaymentMethod('');
        }

        await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tenants/payments/mark-viewed`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        });
        window.dispatchEvent(new Event('tenant-payments:viewed'));
      } catch (err) {
        setError(err.message || 'Failed to load payments.');
      } finally {
        setLoading(false);
      }
    };

    fetchPayments();
  }, [user, navigate, authReady]);

  const handleApproveCharge = async (ledgerId) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tenants/rent-ledger/${ledgerId}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${user.token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Unable to approve charge');
      setLedgers(prev => prev.map(l => l.id === ledgerId ? { ...l, approval_status: 'approved', approved_at: new Date().toISOString() } : l));
    } catch (err) {
      setError(err.message || 'Unable to approve charge');
    }
  };

  const handleDisputeCharge = async (ledgerId) => {
    const note = window.prompt('Reason for disputing this charge (optional):');
    if (note === null) return; // cancelled
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tenants/rent-ledger/${ledgerId}/dispute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${user.token}` },
        body: JSON.stringify({ note: note || undefined })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Unable to dispute charge');
      setLedgers(prev => prev.map(l => l.id === ledgerId ? { ...l, approval_status: 'disputed', dispute_note: note } : l));
    } catch (err) {
      setError(err.message || 'Unable to dispute charge');
    }
  };

  const [payingLedgerId, setPayingLedgerId] = useState(null);

  const handlePayNow = async (ledgerId) => {
    if (!window.confirm('Pay this rent entry now using your default payment method?')) return;
    setPayingLedgerId(ledgerId);
    setError('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tenants/payments/pay-now`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${user.token}` },
        body: JSON.stringify({ rent_ledger_id: ledgerId })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Payment failed');
      setLedgers(prev => prev.map(l => l.id === ledgerId ? { ...l, status: data.status === 'succeeded' ? 'paid' : 'processing' } : l));
      if (data.status === 'succeeded') {
        setError(null);
        setSuccessMsg('Payment successful! ✅');
        setTimeout(() => { setSuccessMsg(null); refreshPaymentsAndLedgers(user?.token, true); }, 3000);
      }
    } catch (err) {
      setError(err.message || 'Payment failed');
    } finally {
      setPayingLedgerId(null);
    }
  };

  const handleAutopayToggle = async (activeTenantId, value) => {
    if (autopaySaving) return;
    setAutopaySaving(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tenants/autopay`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${user.token}` },
        body: JSON.stringify({ active_tenant_id: activeTenantId, autopay_enabled: value })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to update autopay');
      setLedgers(prev => prev.map(entry => entry.active_tenant_id === activeTenantId ? { ...entry, autopay_enabled: data.autopay?.autopay_enabled } : entry));
    } catch (err) {
      setError(err.message);
    } finally {
      setAutopaySaving(false);
    }
  };

  if (!authReady) return <main className="App-main">Loading payments...</main>;
  if (loading) return <main className="App-main">Loading payments…</main>;

  const today = new Date().toISOString().slice(0, 10);
  const allDue = ledgers.filter(entry => ['due', 'late', 'processing', 'partial'].includes(entry.status) && entry.approval_status !== 'pending' && entry.approval_status !== 'disputed');
  const visibleDue = allDue.filter(entry => !entry.due_date || entry.due_date.slice(0, 10) <= today);
  const upcomingDue = allDue.filter(entry => entry.due_date && entry.due_date.slice(0, 10) > today);
  const pendingConfirmation = ledgers.filter(entry => entry.status === 'pending_confirmation');
  const pendingApproval = ledgers.filter(entry => entry.approval_status === 'pending');
  const disputedCharges = ledgers.filter(entry => entry.approval_status === 'disputed');



  const closeReceipt = () => setReceipt(null);

  const latestPaymentByLedger = payments.reduce((acc, payment) => {
    if (!payment.rent_ledger_id) return acc;
    const existing = acc[payment.rent_ledger_id];
    const existingDate = existing?.created_at ? new Date(existing.created_at).getTime() : 0;
    const nextDate = payment.created_at ? new Date(payment.created_at).getTime() : 0;
    if (!existing || nextDate >= existingDate) {
      acc[payment.rent_ledger_id] = payment;
    }
    return acc;
  }, {});

  return (
    <main className="App-main tenant-dashboard-page">
      <div className="ledger-header">
        <div>
          <p className="eyebrow-tag">Payments</p>
          <h2 style={{ margin: 0 }}>Payments</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.25rem' }}>
            <p style={{ margin: 0 }}>Manage rent due, payment history, and saved methods.</p>
            <InfoChip label="Need help?">
              Toggle autopay per property, and you'll always see fees + method before confirming a payment.
            </InfoChip>
          </div>
        </div>
      </div>

      {/* ── Payment Mode Selector ── */}
      {paymentsReady && <div className="payment-mode-selector">
        {/* Direct payment option */}
        <button
          type="button"
          className={`payment-mode-card${paymentMode === 'off_platform' ? ' payment-mode-card--active-green' : ''}`}
          disabled={paymentModeSaving}
          onClick={() => { if (paymentMode !== 'off_platform') updatePaymentMode('off_platform'); }}
        >
          <div className="payment-mode-card__header">
            <svg className="payment-mode-card__icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M2 10h20"/>
              <rect x="2" y="4" width="20" height="16" rx="2"/>
              <path d="M12 14h4"/>
              <path d="M6 14h2"/>
            </svg>
            <span className="payment-mode-card__title">Pay landlord directly</span>
            {paymentMode === 'off_platform' && (
              <svg className="payment-mode-card__check" width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
              </svg>
            )}
          </div>
          <p className="payment-mode-card__desc">
            e-Transfer, cheque, or cash. Report payments and your landlord confirms.
          </p>
        </button>

        {/* Auto-pay option */}
        <button
          type="button"
          className={`payment-mode-card${paymentMode === 'autopay' ? ' payment-mode-card--active-blue' : ''}`}
          disabled={paymentModeSaving}
          onClick={() => {
            if (paymentMode !== 'autopay') {
              if (paymentMethods.length > 0) {
                updatePaymentMode('autopay');
              } else {
                setActiveTab('payment-methods');
                updatePaymentMode('autopay');
              }
            }
          }}
        >
          <div className="payment-mode-card__header">
            <svg className="payment-mode-card__icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="1" y="4" width="22" height="16" rx="2"/>
              <path d="M1 10h22"/>
              <path d="M7.5 15h0"/>
              <path d="M15 15l2-2 2 2"/>
            </svg>
            <span className="payment-mode-card__title">Auto-pay</span>
            {paymentMode === 'autopay' && (
              <svg className="payment-mode-card__check" width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
              </svg>
            )}
            {paymentMode !== 'autopay' && paymentMethods.length === 0 && (
              <span className="payment-mode-card__hint">Setup required</span>
            )}
          </div>
          <p className="payment-mode-card__desc">
            Card or bank account (PAD). Rent is charged automatically on the due date.
          </p>
        </button>
      </div>}

      {paymentsReady && paymentMode === 'pending' && (
        <p className="status-text" style={{ marginTop: '0.5rem', textAlign: 'center', fontStyle: 'italic' }}>
          Choose how you'd like to pay rent
        </p>
      )}

      {successMsg && <p className="success-message" style={{ color: '#059669', fontWeight: 600, marginTop: '0.5rem' }}>{successMsg}</p>}
      {error && <p className="error-message">{error}</p>}
      {!paymentsReady && (
        <div className="dashboard-section" style={{ marginTop: '0.75rem', borderLeft: '4px solid #f5a623', background: 'var(--surface-color, #fff7e6)', padding: '0.75rem' }}>
          <p className="status-text" style={{ margin: 0 }}>Your landlord hasn't enabled online payments yet. They'll be notified.</p>
        </div>
      )}

      <div className="subnav-wrap" style={{ marginTop: '0.75rem' }}>
        <nav className="analytics-subnav" aria-label="Payments sections">
          <button type="button" className={activeTab === 'rent-due' ? 'primary-button' : 'secondary-button'} onClick={() => setActiveTab('rent-due')}>Rent due</button>
          <button type="button" className={activeTab === 'payment-history' ? 'primary-button' : 'secondary-button'} onClick={() => setActiveTab('payment-history')}>Payment history</button>
          <button type="button" className={activeTab === 'payment-methods' ? 'primary-button' : 'secondary-button'} onClick={() => setActiveTab('payment-methods')}>Payment methods</button>
        </nav>
      </div>

      {activeTab === 'rent-due' && (
      <div className="dashboard-section">

        {pendingConfirmation.length > 0 && (
          <div className="dashboard-section" style={{ marginBottom: '1rem', borderLeft: '4px solid #f59e0b', background: 'var(--surface-color, #fffbeb)', padding: '1rem', borderRadius: '10px' }}>
            <h3 style={{ margin: '0 0 0.5rem' }}>Pending landlord confirmation</h3>
            <p className="status-text">You reported these payments. Waiting for your landlord to confirm.</p>
            <div className="ledger-grid" style={{ marginTop: '0.75rem' }}>
              {pendingConfirmation.map(entry => (
                <div key={`pc-${entry.id}`} className="ledger-item">
                  <div className="ledger-item-header">
                    <h3>{formatCurrency(entry.amount)}</h3>
                    <span className="status-pill" style={{ background: '#f59e0b', color: '#fff' }}>Pending confirmation</span>
                  </div>
                  <p><strong>Due:</strong> {entry.due_date ? new Date(entry.due_date).toLocaleDateString() : '-'}</p>
                  <p><strong>Property:</strong> {entry.property_address || '-'}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {pendingApproval.length > 0 && (
          <div className="dashboard-section" style={{ marginBottom: '1rem', borderLeft: '4px solid #f5a623', background: 'var(--surface-color, #fff7e6)', padding: '1rem', borderRadius: '10px' }}>
            <h3 style={{ margin: '0 0 0.5rem' }}>Charges requiring your approval</h3>
            <p className="status-text">These charges were added by your landlord and need your review before they can be collected.</p>
            <div className="ledger-grid" style={{ marginTop: '0.75rem' }}>
              {pendingApproval.map(entry => (
                <div key={entry.id} className="ledger-item">
                  <div className="ledger-item-header">
                    <h3>{formatCurrency(entry.amount)}</h3>
                    <span className="status-pill" style={{ background: '#f5a623', color: '#fff' }}>Pending approval</span>
                  </div>
                  <p><strong>Type:</strong> {(entry.charge_type || 'charge').replace('_', ' ')}</p>
                  {entry.approval_note && <p className="status-text">{entry.approval_note}</p>}
                  <p><strong>Due:</strong> {entry.due_date ? new Date(entry.due_date).toLocaleDateString() : '-'}</p>
                  <div className="form-actions" style={{ marginTop: '0.75rem' }}>
                    <button className="primary-button" onClick={() => handleApproveCharge(entry.id)}>
                      Approve charge
                    </button>
                    <button className="secondary-button" onClick={() => handleDisputeCharge(entry.id)}>
                      Dispute
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {disputedCharges.length > 0 && (
          <div className="dashboard-section" style={{ marginBottom: '1rem', borderLeft: '4px solid #ef4444', background: 'var(--surface-color, #fff5f5)', padding: '1rem', borderRadius: '10px' }}>
            <h3 style={{ margin: '0 0 0.5rem' }}>Disputed charges</h3>
            <div className="ledger-grid" style={{ marginTop: '0.5rem' }}>
              {disputedCharges.map(entry => (
                <div key={entry.id} className="ledger-item">
                  <div className="ledger-item-header">
                    <h3>{formatCurrency(entry.amount)}</h3>
                    <span className="status-pill" style={{ background: '#ef4444', color: '#fff' }}>Disputed</span>
                  </div>
                  <p><strong>Type:</strong> {(entry.charge_type || 'charge').replace('_', ' ')}</p>
                  {entry.dispute_note && <p className="status-text">Your reason: {entry.dispute_note}</p>}
                  <p className="status-text">Your landlord has been notified. This charge is on hold.</p>
                </div>
              ))}
            </div>
          </div>
        )}

        <h3>Rent Payments Due</h3>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <p className="status-text" style={{ margin: 0 }}>Outstanding payments.</p>
          <button className="secondary-button" onClick={() => refreshPaymentsAndLedgers(user?.token)} disabled={paymentRefreshLoading}>
            {paymentRefreshLoading ? 'Refreshing…' : 'Refresh status'}
          </button>
        </div>
        <InfoChip label="Rent tips">
          Turn on autopay to avoid late fees. You'll always see fees before confirming a payment.
        </InfoChip>

        {visibleDue.length === 0 ? (
          <p className="status-text">No outstanding payments.</p>
        ) : (
          <>
          {paymentsReady && (
            <div style={{ marginTop: '0.75rem', marginBottom: '0.75rem' }}>
              <p className="status-text" style={{ marginBottom: '0.5rem' }}>Payment method:</p>
              {paymentMethods.length === 0 ? (
                <p className="status-text">No saved methods yet.</p>
              ) : (
                <select
                  value={selectedPaymentMethod}
                  onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                  className="form-control"
                  style={{ maxWidth: '320px' }}
                >
                  {paymentMethods.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.label || m.type} {m.last4 ? `•••• ${m.last4}` : ''}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}
          <div className="ledger-grid">
            {visibleDue.map(entry => {
              const passThroughCardFee = entry.pass_through_card_fee ?? entry.pass_card_fee_to_tenant ?? true;
              const latestPayment = latestPaymentByLedger[entry.id];
              const hasFailedPayment = latestPayment?.status === 'failed';
              const hasDispute = latestPayment?.status === 'disputed';
              const hasRefund = latestPayment?.status === 'refunded' || latestPayment?.status === 'partially_refunded';
              const statusPill = hasFailedPayment
                ? 'failed'
                : hasDispute
                  ? 'disputed'
                  : hasRefund
                    ? latestPayment.status
                    : entry.status;
              const statusLabel = statusPill.replace('_', ' ');
              const failureReason = latestPayment?.failure_reason;
              const disputeNote = latestPayment?.dispute_status ? `Dispute: ${latestPayment.dispute_status}` : null;
              const refundNote = latestPayment?.refund_status ? `Refund: ${latestPayment.refund_status}` : null;
              const payLabel = hasFailedPayment ? 'Retry payment' : entry.status === 'processing' ? 'Processing…' : 'Pay now';

              const remainingBalance = entry.status === 'partial'
                ? Number(entry.amount || 0) - Number(entry.amount_paid || 0)
                : Number(entry.amount || 0);
              const hasLateFee = entry.late_fee_applied && Number(entry.late_fee_amount || 0) > 0;

              // Calculate processing fee if passed through
              const cardFeeAmount = passThroughCardFee
                ? Math.max(0, (remainingBalance * 0.029 + 0.30) / (1 - 0.029))
                : 0;
              const totalWithFees = remainingBalance + cardFeeAmount;

              return (
              <div key={entry.id} className="ledger-item">
                <div className="ledger-item-header">
                  <h3>{formatCurrency(entry.amount)}</h3>
                  <span className={`status-pill ${statusPill}`}>{statusLabel}</span>
                </div>
                {entry.status === 'partial' && (
                  <div className="dashboard-section" style={{ marginTop: '0.35rem', borderLeft: '4px solid #f5a623', background: 'var(--surface-color, #fff7e6)', padding: '0.5rem 0.75rem' }}>
                    <p className="status-text" style={{ margin: 0 }}>
                      Paid so far: {formatCurrency(entry.amount_paid || 0)} · Remaining: <strong>{formatCurrency(remainingBalance)}</strong>
                    </p>
                  </div>
                )}
                {hasLateFee && (
                  <div className="dashboard-section" style={{ marginTop: '0.35rem', borderLeft: '4px solid #ef4444', background: 'var(--surface-color, #fff5f5)', padding: '0.5rem 0.75rem' }}>
                    <p className="status-text" style={{ margin: 0 }}>
                      Late fee of {formatCurrency(entry.late_fee_amount)} applied
                    </p>
                  </div>
                )}
                <p><strong>Due:</strong> {entry.due_date ? new Date(entry.due_date).toLocaleDateString() : '-'}</p>
                {passThroughCardFee && (
                  <div className="status-text" style={{ marginTop: '0.25rem' }}>
                    <p style={{ margin: 0 }}>Processing fee: {formatCurrency(cardFeeAmount)}</p>
                    <p style={{ margin: 0 }}><strong>Total: {formatCurrency(totalWithFees)}</strong></p>
                  </div>
                )}
                {latestPayment && latestPayment.status && latestPayment.status !== 'succeeded' && (
                  <p className="status-text">Last payment: {latestPayment.status.replace('_', ' ')}</p>
                )}
                {hasFailedPayment && failureReason && (
                  <p className="error-message">Failure reason: {failureReason}</p>
                )}
                {hasDispute && disputeNote && (
                  <p className="status-text">{disputeNote}{latestPayment?.dispute_reason ? ` · ${latestPayment.dispute_reason}` : ''}</p>
                )}
                {hasRefund && refundNote && (
                  <p className="status-text">{refundNote}{latestPayment?.refund_amount ? ` · ${formatCurrency(latestPayment.refund_amount / 100)}` : ''}</p>
                )}
                {paymentsReady && paymentMode === 'autopay' && (
                  <label className="status-text" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem' }}>
                    <input
                      type="checkbox"
                      checked={entry.autopay_enabled !== false}
                      onChange={(e) => handleAutopayToggle(entry.active_tenant_id, e.target.checked)}
                      disabled={autopaySaving}
                    />
                    Autopay - automatically pay rent on due date
                  </label>
                )}
                {paymentsReady && paymentMethods.length > 0 && (
                  <div style={{ marginTop: '0.5rem' }}>
                    <button className="primary-button" onClick={() => handlePayNow(entry.id)} disabled={ledgerLoading || payingLedgerId === entry.id || entry.status === 'processing'}>
                      {payingLedgerId === entry.id ? 'Processing…' : payLabel}
                    </button>
                  </div>
                )}
                {paymentsReady && paymentMethods.length === 0 && paymentMode === 'autopay' && (
                  <p className="status-text" style={{ marginTop: '0.5rem' }}>
                    <button className="ghost-button" style={{ fontSize: '0.85rem', padding: '0.3rem 0.65rem' }} onClick={() => setActiveTab('payment-methods')}>
                      Add a payment method to pay online
                    </button>
                  </p>
                )}
                {['due', 'late'].includes(entry.status) && (
                  <div style={{ marginTop: '0.35rem' }}>
                    <button
                      className="ghost-button"
                      style={{ fontSize: '0.85rem', padding: '0.3rem 0.65rem' }}
                      onClick={() => { setReportPaidModal(entry); setReportPaidMethod('e_transfer'); setReportPaidReference(''); setReportPaidNote(''); }}
                    >
                      I already paid my landlord
                    </button>
                  </div>
                )}
                {entry.status === 'processing' && (
                  <p className="status-text">Payment processing. Refresh status in a few seconds.</p>
                )}
                {entry.status === 'failed' && (
                  <p className="error-message">Payment failed. Please try again.</p>
                )}
              </div>
            );
          })}
          </div>
          </>
        )}

        {upcomingDue.length > 0 && (
          <div style={{ marginTop: '1.5rem' }}>
            <h3>Upcoming Rent</h3>
            <p className="status-text">Future payments — no action needed yet.</p>
            <div className="ledger-grid" style={{ marginTop: '0.5rem', opacity: 0.7 }}>
              {upcomingDue.map(entry => (
                <div key={entry.id} className="ledger-item">
                  <div className="ledger-item-header">
                    <h3>{formatCurrency(entry.amount)}</h3>
                    <span className="status-pill" style={{ background: 'var(--light-text-color, #9ca3af)', color: '#fff' }}>Upcoming</span>
                  </div>
                  <p><strong>Due:</strong> {entry.due_date ? new Date(entry.due_date).toLocaleDateString('en-CA', { weekday: 'short', month: 'long', day: 'numeric', year: 'numeric' }) : '-'}</p>
                  <p><strong>Property:</strong> {entry.property_address || '-'}</p>
                  {entry.autopay_enabled && <p className="status-text">✓ Autopay enabled</p>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      )}

      {activeTab === 'payment-history' && (
      <div className="dashboard-section">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3>Payment History</h3>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="secondary-button" onClick={() => {
              const now = new Date();
              const url = `${process.env.REACT_APP_API_BASE_URL}/api/tenants/payments/statement?year=${now.getFullYear()}&month=${now.getMonth() + 1}`;
              fetch(url, { headers: { Authorization: `Bearer ${user.token}` } })
                .then(r => { if (!r.ok) throw new Error('Failed'); return r.blob(); })
                .then(blob => { const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `rent-statement-${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}.pdf`; a.click(); })
                .catch(() => setError('Unable to download statement. Please try again.'));
            }}>
              This month
            </button>
            <button className="secondary-button" onClick={() => {
              const url = `${process.env.REACT_APP_API_BASE_URL}/api/tenants/payments/statement?year=${new Date().getFullYear()}`;
              fetch(url, { headers: { Authorization: `Bearer ${user.token}` } })
                .then(r => { if (!r.ok) throw new Error('Failed'); return r.blob(); })
                .then(blob => { const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `rent-statement-${new Date().getFullYear()}.pdf`; a.click(); })
                .catch(() => setError('Unable to download statement. Please try again.'));
            }}>
              Year PDF
            </button>
          </div>
        </div>
        {payments.length > 0 && (() => {
          const succeeded = payments.filter(p => p.status === 'succeeded');
          const totalPaid = succeeded.reduce((sum, p) => sum + Number(p.amount || 0), 0);
          const thisYear = new Date().getFullYear();
          const ytd = succeeded.filter(p => p.paid_at && new Date(p.paid_at).getFullYear() === thisYear);
          const ytdTotal = ytd.reduce((sum, p) => sum + Number(p.amount || 0), 0);
          return (
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
              <div className="dashboard-section" style={{ flex: '1 1 140px', padding: '0.75rem', textAlign: 'center', borderLeft: '3px solid #2f6fed' }}>
                <p className="status-text" style={{ margin: 0, fontSize: '0.75rem' }}>Total Paid</p>
                <p style={{ margin: '0.25rem 0 0', fontWeight: '700', fontSize: '1.1rem' }}>{formatCurrency(totalPaid)}</p>
              </div>
              <div className="dashboard-section" style={{ flex: '1 1 140px', padding: '0.75rem', textAlign: 'center', borderLeft: '3px solid #059669' }}>
                <p className="status-text" style={{ margin: 0, fontSize: '0.75rem' }}>{thisYear} YTD</p>
                <p style={{ margin: '0.25rem 0 0', fontWeight: '700', fontSize: '1.1rem' }}>{formatCurrency(ytdTotal)}</p>
              </div>
              <div className="dashboard-section" style={{ flex: '1 1 140px', padding: '0.75rem', textAlign: 'center', borderLeft: '3px solid #6b7280' }}>
                <p className="status-text" style={{ margin: 0, fontSize: '0.75rem' }}>Payments</p>
                <p style={{ margin: '0.25rem 0 0', fontWeight: '700', fontSize: '1.1rem' }}>{succeeded.length}</p>
              </div>
            </div>
          );
        })()}
        {payments.length === 0 ? (
          <p className="status-text">No payment history yet.</p>
        ) : (
          <div className="ledger-grid">
            {payments.map(payment => (
              <div key={payment.id} className="ledger-item">
                <div className="ledger-item-header">
                  <h3>{formatCurrency(payment.amount)}</h3>
                  <span className={`status-pill ${payment.status}`}>{payment.status}</span>
                </div>
                <p><strong>Property:</strong> {payment.property_address || '-'}</p>
                <p><strong>Due:</strong> {payment.due_date ? new Date(payment.due_date).toLocaleDateString() : '-'}</p>
                <p><strong>Paid:</strong> {payment.paid_at ? new Date(payment.paid_at).toLocaleDateString() : '-'}</p>
                <p><strong>Method:</strong> {payment.payment_method_label || payment.payment_method_type || '-'} {payment.payment_method_last4 ? `•••• ${payment.payment_method_last4}` : ''}</p>
                {payment.failure_reason && <p className="status-text">Failure reason: {payment.failure_reason}</p>}
                {payment.dispute_status && <p className="status-text">Dispute: {payment.dispute_status}</p>}
                {payment.refund_status && (
                  <p className="status-text">Refund: {payment.refund_status}{payment.refund_amount ? ` · ${formatCurrency(payment.refund_amount / 100)}` : ''}</p>
                )}
                {Array.isArray(payment.fee_events) && payment.fee_events.length > 0 && (
                  <div className="status-text" style={{ marginTop: '0.35rem' }}>
                    <strong>Fee events:</strong>
                    <ul style={{ margin: '0.35rem 0 0', paddingLeft: '1.25rem' }}>
                      {payment.fee_events.map((fee) => (
                        <li key={fee.id || `${fee.fee_type}-${fee.created_at}`}>
                          {fee.fee_type === 'pad_failure_fee' ? 'PAD failure fee' : fee.fee_type} · {formatCurrency(fee.amount || 0)} · {fee.charged_to || 'n/a'}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <button className="secondary-button" onClick={() => setReceipt(payment)} style={{ marginTop: '0.5rem' }}>
                  View Receipt
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      )}

      {activeTab === 'payment-methods' && (
      <div className="dashboard-section">
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0 }}>Payment Methods</h3>
          <InfoChip label="Method help">
            Cards run instantly; PAD pulls from your bank with no card fee. You can delete either anytime.
          </InfoChip>
        </div>
        <p className="status-text" style={{ marginTop: '0.35rem' }}>Autopay and one-click payments use your default method.</p>
        {paymentMethods.length === 0 ? (
          <p className="status-text">No saved methods yet.</p>
        ) : (
          <div className="ledger-grid">
            {paymentMethods.map((method) => (
              <div key={method.id} className="ledger-item">
                <div className="ledger-item-header" style={{ alignItems: 'center', gap: '0.5rem' }}>
                  <h3 style={{ margin: 0 }}>{method.label || method.type}</h3>
                  <span className="report-chip report-chip--muted">{method.type?.toUpperCase() || 'METHOD'}</span>
                  {method.is_default && <span className="status-pill approved">Default</span>}
                </div>
                <p>{method.brand || method.bank_name || '-'} {method.last4 ? `•••• ${method.last4}` : ''}</p>
                <div className="form-actions" style={{ marginTop: '0.75rem' }}>
                  <button
                    className="secondary-button"
                    disabled={method.is_default}
                    onClick={async () => {
                      try {
                        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tenants/payment-methods/${method.id}/default`, {
                          method: 'PUT',
                          headers: { Authorization: `Bearer ${user.token}` }
                        });
                        const data = await res.json().catch(() => ({}));
                        if (!res.ok) throw new Error(data.error || 'Unable to update default.');
                        setPaymentMethods(prev => prev.map(m => ({ ...m, is_default: m.id === method.id })));
                      } catch (err) {
                        setError(err.message || 'Unable to update default.');
                      }
                    }}
                  >
                    {method.is_default ? 'Default' : 'Make default'}
                  </button>
                  <button
                    className="secondary-button"
                    onClick={async () => {
                      try {
                        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tenants/payment-methods/${method.id}`, {
                          method: 'DELETE',
                          headers: { Authorization: `Bearer ${user.token}` }
                        });
                        const data = await res.json().catch(() => ({}));
                        if (!res.ok) throw new Error(data.error || 'Unable to remove method.');
                        await handleMethodRemoved(method.id);
                      } catch (err) {
                        setError(err.message || 'Unable to remove method.');
                      }
                    }}
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {paymentsReady && (
          <div className="dashboard-section" style={{ marginTop: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.25rem' }}>
              <h4 style={{ margin: 0 }}>Add a card</h4>
              <InfoChip label="Card info">
                Use a debit or credit card issued in Canada. You can remove it anytime and see fees before confirming.
              </InfoChip>
            </div>
            <Elements stripe={stripePromise}>
              <CardSetupForm token={user?.token} onAdded={handleMethodAdded} />
            </Elements>
            <PadSetupForm token={user?.token} onAdded={handleMethodAdded} />
          </div>
        )}
      </div>
      )}

      {receipt && (
        <div className="image-modal-overlay" onClick={closeReceipt}>
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="image-modal-close" onClick={closeReceipt}>×</button>
            <h3>Payment Receipt</h3>
            <p><strong>Amount:</strong> {formatCurrency(receipt.amount)}</p>
            <p><strong>Date paid:</strong> {receipt.paid_at ? new Date(receipt.paid_at).toLocaleString() : '—'}</p>
            <p><strong>Property:</strong> {receipt.property_address || '—'}</p>
            <p><strong>Due date:</strong> {receipt.due_date ? new Date(receipt.due_date).toLocaleDateString() : '—'}</p>
            <p><strong>Payment method:</strong> {receipt.payment_method_label || receipt.payment_method_type || '—'} {receipt.payment_method_last4 ? `•••• ${receipt.payment_method_last4}` : ''}</p>
            {receipt.provider_reference && <p><strong>Reference:</strong> {receipt.provider_reference}</p>}
          </div>
        </div>
      )}

      {reportPaidModal && (
        <div className="image-modal-overlay" onClick={() => setReportPaidModal(null)}>
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '400px' }}>
            <button className="image-modal-close" onClick={() => setReportPaidModal(null)}>×</button>
            <h3>Report payment to landlord</h3>
            <p className="status-text">
              {formatCurrency(reportPaidModal.amount)} due {reportPaidModal.due_date ? new Date(reportPaidModal.due_date).toLocaleDateString() : ''}
            </p>
            <div className="form-group" style={{ marginTop: '0.75rem' }}>
              <label>Payment method</label>
              <select className="form-control" value={reportPaidMethod} onChange={(e) => setReportPaidMethod(e.target.value)} disabled={reportPaidLoading}>
                <option value="e_transfer">e-Transfer</option>
                <option value="cheque">Cheque</option>
                <option value="cash">Cash</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="form-group" style={{ marginTop: '0.5rem' }}>
              <label>Reference # <span className="status-text">(optional)</span></label>
              <input className="form-control" placeholder="e.g. e-Transfer confirmation #" value={reportPaidReference} onChange={(e) => setReportPaidReference(e.target.value)} disabled={reportPaidLoading} />
            </div>
            <div className="form-group" style={{ marginTop: '0.5rem' }}>
              <label>Note <span className="status-text">(optional)</span></label>
              <input className="form-control" placeholder="e.g. Paid on Sept 1" value={reportPaidNote} onChange={(e) => setReportPaidNote(e.target.value)} disabled={reportPaidLoading} />
            </div>
            {error && <p className="error-message" style={{ marginTop: '0.5rem' }}>{error}</p>}
            <div className="form-actions" style={{ marginTop: '1rem' }}>
              <button
                className="primary-button"
                disabled={reportPaidLoading}
                onClick={async () => {
                  setReportPaidLoading(true);
                  setError(null);
                  try {
                    const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/tenants/rent-ledger/${reportPaidModal.id}/report-paid`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${user.token}` },
                      body: JSON.stringify({ method: reportPaidMethod, reference: reportPaidReference || undefined, note: reportPaidNote || undefined })
                    });
                    const data = await res.json().catch(() => ({}));
                    if (!res.ok) throw new Error(data.error || 'Unable to report payment');
                    setLedgers(prev => prev.map(l => l.id === reportPaidModal.id ? { ...l, status: 'pending_confirmation' } : l));
                    setReportPaidModal(null);
                  } catch (err) {
                    setError(err.message || 'Unable to report payment');
                  } finally {
                    setReportPaidLoading(false);
                  }
                }}
              >
                {reportPaidLoading ? 'Submitting…' : 'Submit'}
              </button>
              <button className="secondary-button" onClick={() => setReportPaidModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

export default TenantPayments;
